import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Paper,
  Chip,
  IconButton
} from '@mui/material';
import {
  CloudUpload,
  Map,
  PlayArrow,
  CheckCircle,
  Info,
  Close
} from '@mui/icons-material';
import { stationAPI } from '../services/api';

interface StationImportModalProps {
  open: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

interface ImportStep {
  label: string;
  description: string;
}

const IMPORT_STEPS: ImportStep[] = [
  { label: 'Upload File', description: 'Select and upload your Excel file' },
  { label: 'Map Fields', description: 'Map Excel headers to station fields' },
  { label: 'Import', description: 'Process and import stations' }
];

// Core Station Fields
const CORE_FIELDS = {
  name: { label: 'Station Name', required: true, description: 'Name of the station' },
  stationCode: { label: 'Station Code', required: true, description: 'Unique station code (A-Z, 0-9, _)' },
  cityId: { label: 'City ID', required: true, description: 'ID of the city where station is located' },
  lat: { label: 'Latitude', required: true, description: 'Latitude coordinate' },
  lng: { label: 'Longitude', required: true, description: 'Longitude coordinate' },
  description: { label: 'Description', required: false, description: 'Brief description of the station' },
  isInterchange: { label: 'Is Interchange', required: false, description: 'Whether this is an interchange station' },
  firstTrainTime: { label: 'First Train Time', required: false, description: 'First train time (HH:MM format)' },
  lastTrainTime: { label: 'Last Train Time', required: false, description: 'Last train time (HH:MM format)' },
  isActive: { label: 'Is Active', required: false, description: 'Whether the station is active' },
  status: { label: 'Status', required: false, description: 'Station status (OPERATIONAL/MAINTENANCE/CLOSED)' },
  structures: { label: 'Structures & Facilities', required: false, description: 'Comma-separated list of structures (e.g., Elevator,Escalator,Parking)' },
  totalPlatforms: { label: 'Total Platforms', required: false, description: 'Number of platforms at the station' },
  totalTracks: { label: 'Total Tracks', required: false, description: 'Number of tracks at the station' }
};

const REQUIRED_FIELDS = Object.keys(CORE_FIELDS).filter(key => CORE_FIELDS[key as keyof typeof CORE_FIELDS].required);

const StationImportModal: React.FC<StationImportModalProps> = ({
  open,
  onClose,
  onImportComplete
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{
    totalRows: number;
    created: number;
    updated: number;
    errors: number;
    errorMessages: string[];
    status: string;
    processingTime: number;
    message: string;
  } | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith('.xlsx') && !selectedFile.name.toLowerCase().endsWith('.xls')) {
      setError('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  const handleGetHeaders = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const response = await stationAPI.getFileHeaders(file);
      setHeaders(response.data);
      
      // Auto-map fields to headers
      const autoMapping: Record<string, string> = {};
      Object.keys(CORE_FIELDS).forEach(field => {
        const fieldInfo = CORE_FIELDS[field as keyof typeof CORE_FIELDS];
        const matchingHeader = response.data.find(header => {
          const lowerHeader = header.toLowerCase();
          return lowerHeader.includes(fieldInfo.label.toLowerCase()) || 
                 fieldInfo.label.toLowerCase().includes(lowerHeader) ||
                 lowerHeader.includes(field.toLowerCase()) ||
                 field.toLowerCase().includes(lowerHeader);
        });
        if (matchingHeader) {
          autoMapping[matchingHeader] = field;
        }
      });
      setFieldMapping(autoMapping);
      
      setActiveStep(1);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to extract file headers');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    // Validate required fields are mapped
    const mappedFields = Object.values(fieldMapping);
    const missingRequired = REQUIRED_FIELDS.filter(field => !mappedFields.includes(field));
    
    if (missingRequired.length > 0) {
      setError(`Missing required field mappings: ${missingRequired.join(', ')}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await stationAPI.importStations(file, {
        fieldMapping,
        updateExisting: true,
        skipErrors: true
      });

      setImportResult(response.data);
      setActiveStep(2);
      // Don't call onImportComplete here - let user see results first
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to import stations');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setFile(null);
    setHeaders([]);
    setFieldMapping({});
    setError(null);
    setImportResult(null);
    onClose();
  };

  const handleImportComplete = () => {
    onImportComplete();
    handleClose();
  };

  const canProceedToImport = Object.keys(fieldMapping).length > 0 && 
    REQUIRED_FIELDS.every(field => Object.values(fieldMapping).includes(field));

  const getStepIcon = (stepIndex: number) => {
    if (stepIndex < activeStep) return <CheckCircle color="success" />;
    if (stepIndex === activeStep) return <Info color="primary" />;
    return <Info color="disabled" />;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Import Stations from Excel</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {IMPORT_STEPS.map((step, index) => (
            <Step key={step.label}>
              <StepLabel icon={getStepIcon(index)}>
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: File Upload */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>Upload Excel File</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Select an Excel file (.xlsx or .xls) containing station data. The first row should contain headers.
            </Typography>

            <Paper
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main' }
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              
              {file ? (
                <Box>
                  <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    {file.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    File size: {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <CloudUpload sx={{ fontSize: 48, mb: 1, color: 'text.secondary' }} />
                  <Typography variant="h6" gutterBottom>
                    Click to select Excel file
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    or drag and drop here
                  </Typography>
                </Box>
              )}
            </Paper>

            {file && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleGetHeaders}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Map />}
                >
                  {loading ? 'Processing...' : 'Extract Headers & Continue'}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Step 2: Field Mapping */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>Map Excel Headers to Station Fields</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Map each station field to the corresponding Excel column header. Required fields are marked with *. 
              You can map any combination of available fields - only the core required fields are mandatory.
            </Typography>

            {/* Table-like structure for field mapping */}
            <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, overflow: 'hidden' }}>
              {/* Header Row */}
              <Box sx={{ 
                display: 'flex', 
                bgcolor: 'grey.100', 
                borderBottom: '1px solid', 
                borderColor: 'grey.300',
                fontWeight: 'bold'
              }}>
                <Box sx={{ 
                  flex: '0 0 60%', 
                  p: 2, 
                  borderRight: '1px solid', 
                  borderColor: 'grey.300',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Station Field
                  </Typography>
                </Box>
                <Box sx={{ 
                  flex: '0 0 40%', 
                  p: 2, 
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Excel Header Mapping
                  </Typography>
                </Box>
              </Box>

              {/* Core Fields */}
              <Box>
                <Box sx={{ 
                  bgcolor: 'primary.50', 
                  p: 1, 
                  borderBottom: '1px solid', 
                  borderColor: 'grey.300'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Core Fields
                  </Typography>
                </Box>
                {Object.entries(CORE_FIELDS).map(([field, info]) => (
                  <Box key={field} sx={{ 
                    display: 'flex', 
                    borderBottom: '1px solid', 
                    borderColor: 'grey.200',
                    '&:last-child': { borderBottom: 'none' }
                  }}>
                    <Box sx={{ 
                      flex: '0 0 60%', 
                      p: 2, 
                      borderRight: '1px solid', 
                      borderColor: 'grey.300',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '100px' }}>
                          {field}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          ({info.description})
                        </Typography>
                        {info.required && (
                          <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ 
                      flex: '0 0 40%', 
                      p: 2, 
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={Object.keys(fieldMapping).find(key => fieldMapping[key] === field) || ''}
                          onChange={(e) => {
                            const selectedHeader = e.target.value;
                            if (selectedHeader) {
                              // Remove any existing mapping for this field
                              const newMapping = { ...fieldMapping };
                              Object.keys(newMapping).forEach(key => {
                                if (newMapping[key] === field) {
                                  delete newMapping[key];
                                }
                              });
                              // Add new mapping
                              newMapping[selectedHeader] = field;
                              setFieldMapping(newMapping);
                            }
                          }}
                          displayEmpty
                          sx={{ minWidth: '200px' }}
                        >
                          <MenuItem value="">
                            <em>Select Excel header</em>
                          </MenuItem>
                          {headers.map((header) => (
                            <MenuItem key={header} value={header}>
                              {header}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* Step 3: Import Results */}
        {activeStep === 2 && importResult && (
          <Box>
            <Typography variant="h6" gutterBottom>Import Results</Typography>
            
            <Paper sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {importResult.totalRows}
                    </Typography>
                    <Typography variant="body2">Total Rows</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {importResult.created}
                    </Typography>
                    <Typography variant="body2">Created</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="info.main">
                      {importResult.updated}
                    </Typography>
                    <Typography variant="body2">Updated</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color={importResult.errors > 0 ? 'error' : 'success.main'}>
                      {importResult.errors}
                    </Typography>
                    <Typography variant="body2">Errors</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Alert severity={importResult.status === 'SUCCESS' ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {importResult.message}
            </Alert>

            {importResult.errorMessages && importResult.errorMessages.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Error Details:</Typography>
                <Paper sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                  {importResult.errorMessages.map((error, index) => (
                    <Typography key={index} variant="body2" color="error" sx={{ mb: 0.5 }}>
                      • {error}
                    </Typography>
                  ))}
                </Paper>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {activeStep > 0 && activeStep < 2 && (
          <Button onClick={() => setActiveStep(activeStep - 1)}>
            Back
          </Button>
        )}
        
        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!canProceedToImport || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
          >
            {loading ? 'Importing...' : 'Start Import'}
          </Button>
        )}
        
        {activeStep === 2 && (
          <Button variant="contained" onClick={handleImportComplete}>
            Complete Import
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StationImportModal;
